import { SelectMenu } from "@/components/site/SelectMenu";
import { useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { Check } from "lucide-react";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/site/Reveal";
import { PageMeta } from "@/components/site/blocks";
import { liveProducts } from "@/data/products";

const field =
  "h-11 w-full rounded-lg border border-input bg-background px-3 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-foreground focus:outline-none";

const promises = [
  "A walkthrough built around your tools and workflow",
  "Straight answers on security, data and pricing",
  "Help setting up your first workspace",
];

const RequestDemo = () => {
  // ?product=agents (from agents.clearmakes.com) picks that product up front.
  const [params] = useSearchParams();
  const asked = params.get("product");
  const initialProduct = liveProducts.some((p) => p.slug === asked) ? asked! : liveProducts[0]?.slug;
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [error, setError] = useState("");

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget).entries());
    setState("sending");
    setError("");
    try {
      const res = await fetch("/api/demo-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "Something went wrong. Please try again.");
      setState("sent");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
      setState("error");
    }
  };

  return (
    <>
      <PageMeta
        title="Request a demo | ClearMakes"
        description="See a ClearMakes product working on your own use case. Tell us a little about your team and we will set up a walkthrough."
        path="/request-demo"
      />
      <main className="bg-background text-foreground">
        <section className="cm-section">
          <div className="cm-container grid gap-12 lg:grid-cols-2 lg:gap-20">
            <Reveal>
              <p className="cm-eyebrow mb-3">Request a demo</p>
              <h1 className="cm-h2">See it working on your own use case.</h1>
              <p className="cm-lead mt-4">
                Tell us a little about your team. We will reply by email to arrange a walkthrough of the product you are
                interested in.
              </p>
              <ul className="mt-8 space-y-3">
                {promises.map((t) => (
                  <li key={t} className="flex gap-3 text-[15px] text-foreground">
                    <Check className="mt-0.5 h-4 w-4 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.05}>
              {state === "sent" ? (
                <div className="cm-card p-8">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-foreground text-background">
                    <Check className="h-5 w-5" />
                  </span>
                  <h2 className="mt-5 text-2xl font-semibold tracking-[-0.02em]">Request received.</h2>
                  <p className="cm-body mt-2">Thank you. A confirmation is on its way to your inbox, and we will reply by email to arrange a time.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="cm-card space-y-4 p-6 sm:p-8">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="block text-[14px] font-medium">
                      Full name <span aria-hidden="true" className="text-[hsl(0_70%_50%)]">*</span>
                      <input name="name" required minLength={2} placeholder="Jane Cooper" maxLength={120} autoComplete="name" className={`${field} mt-1.5`} />
                    </label>
                    <label className="block text-[14px] font-medium">
                      Work email <span aria-hidden="true" className="text-[hsl(0_70%_50%)]">*</span>
                      <input name="email" type="email" required placeholder="jane@company.com" maxLength={200} autoComplete="email" className={`${field} mt-1.5`} />
                    </label>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="block text-[14px] font-medium">
                      Company
                      <input name="company" maxLength={160} autoComplete="organization" className={`${field} mt-1.5`} />
                    </label>
                    <div className="block text-[14px] font-medium">
                      Team size
                      <SelectMenu
                        name="team_size"
                        label="Team size"
                        options={["Just me", "2-10", "11-50", "51-200", "200+"].map((o) => ({ value: o, label: o }))}
                      />
                    </div>
                  </div>
                  <label className="block text-[14px] font-medium">
                    Phone number <span className="font-normal text-muted-foreground">(optional)</span>
                    <input name="phone" type="tel" inputMode="tel" maxLength={32} autoComplete="tel" pattern="[0-9+()\-\s.]{6,32}" title="Digits, spaces and + ( ) - only" placeholder="+1 555 010 1234" className={`${field} mt-1.5`} />
                  </label>
                  <div className="block text-[14px] font-medium">
                    Product
                    <SelectMenu
                      name="product"
                      label="Product"
                      defaultValue={initialProduct}
                      options={[...liveProducts.map((p) => ({ value: p.slug, label: p.name })), { value: "not-sure", label: "Not sure yet" }]}
                    />
                  </div>
                  <label className="block text-[14px] font-medium">
                    What would you like to see? <span className="font-normal text-muted-foreground">(optional)</span>
                    <textarea name="message" rows={4} maxLength={2000} className={`${field} mt-1.5 h-auto py-2.5`} />
                  </label>
                  {/* Honeypot: real people never see or fill this. */}
                  <input name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
                  {state === "error" && (
                    <p role="alert" className="text-[14px] text-destructive">
                      {error}
                    </p>
                  )}
                  <p className="text-[13px] text-muted-foreground"><span aria-hidden="true" className="text-[hsl(0_70%_50%)]">*</span> Required</p>
                  <button type="submit" disabled={state === "sending"} className="cm-btn-primary cm-btn-lg w-full disabled:opacity-60">
                    {state === "sending" ? "Sending…" : "Request a demo"}
                  </button>
                  <p className="text-[13px] text-muted-foreground">
                    We use these details only to reply to your request. See our{" "}
                    <a href="/privacy" className="cm-link">
                      privacy policy
                    </a>
                    .
                  </p>
                </form>
              )}
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default RequestDemo;
